import {useMemo} from "react";

/**
 * Returns the selected categories hierarchy and the sub categories of the selected category
 *
 * @template T                      category type
 * @param allCategories             all categories
 * @param selectedCategoryId        selected category id
 * @returns {{selectedCategoriesHierarchy: T[], subCategories: T[]}}
 */
const useSelectedCategory = (allCategories = [], selectedCategoryId = undefined) => {
    const {getSelectedCategoriesHierarchy, getSubCategories} = useMemo(() =>
            selectedCategoryHook(allCategories, selectedCategoryId),
        [allCategories, selectedCategoryId]);

    const selectedCategoriesHierarchy = useMemo(() => getSelectedCategoriesHierarchy(), [getSelectedCategoriesHierarchy])
    const subCategories = useMemo(() => getSubCategories(), [getSubCategories])

    return {selectedCategoriesHierarchy, subCategories};
}

/**
 * The state management logic for the selected category
 *
 * @template T                  category type
 * @param allCategories         all categories
 * @param selectedCategoryId    selected category id
 * @returns {{getSelectedCategoriesHierarchy: (function(number=): T[]), getSubCategories: (function(number=):(T[]))}}
 */
export const selectedCategoryHook = (allCategories = [], selectedCategoryId = undefined) => {

    /**
     * Fetches the sub categories of the provided category id
     * @param categoryId        selected category id
     * @returns {T[]}
     */
    const getSubCategories = (categoryId = selectedCategoryId) => {
        if (!categoryId) {
            return allCategories.filter(category => !category.parentId);
        }
        return allCategories.filter(category => category.parentId === categoryId);
    };

    /**
     * Returns the selected categories hierarchy
     * @param categoryId      selected category id
     * @returns {T[]}
     */
    const getSelectedCategoriesHierarchy = (categoryId = selectedCategoryId) => {
        const selectedCategories = [];
        let currentCategory = allCategories.find(e => e.id === categoryId);
        while (currentCategory) {
            // eslint-disable-next-line no-loop-func
            const parent = allCategories.find(e => e.id === currentCategory.parentId);
            selectedCategories.unshift({
                ...currentCategory,
                options: getSubCategories(parent?.id ?? null),
            });
            currentCategory = parent;
        }
        return selectedCategories;
    };

    return {getSelectedCategoriesHierarchy, getSubCategories};
}

export default useSelectedCategory;
